import { useState } from "react";
import clsx from "clsx";
import type { VideoZoneDto, VideoZoneStatus } from "../types/videoApi";
import { useNow } from "../hooks/useNow";
import { formatDuration } from "../lib/historyFormat";
import CameraVideoModal from "./CameraVideoModal";

interface ZoneListProps {
  zones: VideoZoneDto[];
  /** 응답 생성 시각(generatedAt). 경과 시간 계산의 앵커 — 클라이언트 시계 오차 방지. */
  referenceNow?: string | null;
  /** 현장 GPU 무신호(연결 끊김) — true 면 zones 상태(OK/PROBLEM)는 낡은 값이라 '신호 없음'(회색)으로 표시. */
  stale?: boolean;
}

/** mismatchSince(시작 시각) 와 기준 시각으로 PROBLEM 지속 초를 계산. null 이면 null. */
function elapsedSec(
  mismatchSince: string | null,
  anchorMs: number,
): number | null {
  if (!mismatchSince) return null;
  const startMs = new Date(mismatchSince).getTime();
  if (Number.isNaN(startMs)) return null;
  return Math.max(0, Math.floor((anchorMs - startMs) / 1000));
}

/**
 * 구역 상태 4종의 표시 스타일 (계약 §1). 정상 3종은 색·라벨로 구분하되 PROBLEM 만 '문제'.
 * tailwind.config 신규 토큰 없이 기존 status-* 토큰 + 내장 sky/amber 팔레트 재사용.
 *   MATCH            → operational(녹)
 *   PARKING_EXPECTED → sky(파랑) ↑   (영상이 더 많음 — 주차 들어오는 중)
 *   LEAVING_EXPECTED → amber(앰버) ↓ (영상이 더 적음 — 출차 빠지는 중)
 *   PROBLEM          → degraded(현행 약한 주황 경고 유지)
 */
const ZONE_VISUAL: Record<
  VideoZoneStatus,
  { label: string; box: string; dot: string }
> = {
  MATCH: {
    label: "정상",
    box: clsx(
      "border-surface-border bg-white text-slate-600",
      "dark:border-surface-darkBorder dark:bg-slate-900/40 dark:text-slate-300",
    ),
    dot: "bg-status-operational dark:bg-status-operational-dark",
  },
  PARKING_EXPECTED: {
    label: "주차 예상 중 ↑",
    box: clsx(
      "border-sky-200 bg-sky-50 text-sky-700",
      "dark:border-sky-900/40 dark:bg-sky-950/40 dark:text-sky-300",
    ),
    dot: "bg-sky-500 dark:bg-sky-400",
  },
  LEAVING_EXPECTED: {
    label: "출차 예상 중 ↓",
    box: clsx(
      "border-amber-200 bg-amber-50 text-amber-700",
      "dark:border-amber-900/40 dark:bg-amber-950/40 dark:text-amber-300",
    ),
    dot: "bg-amber-500 dark:bg-amber-400",
  },
  PROBLEM: {
    label: "문제",
    box: clsx(
      "border-status-degraded/30 bg-status-degraded-bg text-status-degraded-fg",
      "dark:border-status-degraded-dark/30 dark:bg-status-degraded-darkBg dark:text-status-degraded-darkFg",
    ),
    dot: "bg-status-degraded dark:bg-status-degraded-dark",
  },
};

/**
 * 영상분석 lot 의 구역(zone) 목록. BridgeRow 안에서 기존 층(floor) 영역을 대체한다.
 *
 *   ┌────────────────────────────────────────────────────┐
 *   │ ● [📷V1] [📷V2]  [A1]            정상               │
 *   │ 🔺 [📷V2]        [A2]   문제 · 1시간 5분 지속        │
 *   │ ● [📷V3]         [A3]            정상               │
 *   └────────────────────────────────────────────────────┘
 *
 * - 영상분석은 "어느 카메라"가 핵심이라 **카메라를 주 라벨**(클릭 가능)로, 구역은
 *   보조 badge 로 둔다. 카메라 매핑이 없으면 구역명으로 폴백.
 * - 카메라 칩을 클릭하면 영상 뷰어 모달이 열린다. 현재는 스트림 미연결이라
 *   placeholder 화면을 띄우며, 추후 스트림 연동 시 모달 내부만 교체하면 된다.
 * - 지속시간은 formatDuration 으로 초/분/시간 단위 표기(시간 넘어가도 정상).
 * - 색만으로 의미를 전달하지 않도록 "정상/문제" 텍스트를 항상 렌더한다 (a11y).
 */
export default function ZoneList({ zones, referenceNow, stale }: ZoneListProps) {
  const [openCamera, setOpenCamera] = useState<{
    cameraId: string;
    zoneName: string;
  } | null>(null);

  // 매초 리렌더해 지속 시간이 매끄럽게 증가하도록. 앵커는 generatedAt(없으면 now).
  const now = useNow();
  const referenceMs = referenceNow ? new Date(referenceNow).getTime() : now;
  const anchorMs =
    !Number.isNaN(referenceMs) ? Math.max(referenceMs, now) : now;

  return (
    <>
      <ul className="mt-2 space-y-1" aria-label="구역 상태">
        {zones.map((z) => {
          const isMismatch = z.status !== "MATCH";
          const visual = ZONE_VISUAL[z.status];
          const sinceSec = elapsedSec(z.mismatchSince, anchorMs);

          return (
            <li
              key={z.zone}
              className={clsx(
                "flex flex-wrap items-center gap-x-2 gap-y-1 rounded-md border px-2.5 py-1.5",
                stale
                  ? clsx(
                      "border-slate-200 bg-slate-50 text-slate-400",
                      "dark:border-slate-700 dark:bg-slate-800/40 dark:text-slate-500",
                    )
                  : visual.box,
              )}
            >
              {/* 상태 점 */}
              <span
                aria-hidden="true"
                className={clsx(
                  "inline-block h-2 w-2 flex-shrink-0 rounded-full",
                  stale ? "bg-slate-300 dark:bg-slate-600" : visual.dot,
                )}
              />

              {/* 카메라 = 주 라벨(클릭 시 영상 모달). 카메라 매핑 없으면 구역명으로 폴백. */}
              {z.cameras.length > 0 ? (
                <span className="flex flex-wrap items-center gap-1.5">
                  {z.cameras.map((cam) => (
                    <button
                      key={cam}
                      type="button"
                      onClick={() =>
                        setOpenCamera({ cameraId: cam, zoneName: z.zone })
                      }
                      aria-label={`카메라 ${cam} 영상 보기`}
                      className={clsx(
                        "inline-flex items-center gap-1 rounded-full border px-2 py-0.5 font-mono text-[11px] transition-colors",
                        "focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:ring-slate-400",
                        "border-surface-border bg-white text-slate-600 hover:bg-slate-100",
                        "dark:border-surface-darkBorder dark:bg-slate-800/60 dark:text-slate-200 dark:hover:bg-slate-700/60",
                      )}
                    >
                      {/* 카메라 아이콘 */}
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.8"
                        className="h-3 w-3"
                        aria-hidden="true"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M15.75 10.5l4.72-4.72a.75.75 0 0 1 1.28.53v11.38a.75.75 0 0 1-1.28.53l-4.72-4.72M4.5 18.75h9a2.25 2.25 0 0 0 2.25-2.25v-9a2.25 2.25 0 0 0-2.25-2.25h-9A2.25 2.25 0 0 0 2.25 7.5v9a2.25 2.25 0 0 0 2.25 2.25Z"
                        />
                      </svg>
                      {cam}
                    </button>
                  ))}
                </span>
              ) : (
                <span className="text-[13px] font-medium">{z.zone}</span>
              )}

              {/* 구역 = 보조 badge (카메라가 있을 때만) */}
              {z.cameras.length > 0 && (
                <span className="rounded-full border border-surface-border bg-white/60 px-2 py-0.5 text-[11px] font-medium dark:border-surface-darkBorder dark:bg-slate-900/40">
                  {z.zone}
                </span>
              )}

              {/* 상태 + 지속(우측). stale 이면 상태 판단이 낡았으므로 '신호 없음'. */}
              <span className="ml-auto text-xs">
                {stale ? "신호 없음" : visual.label}
                {!stale && isMismatch && sinceSec != null && (
                  <span className="ml-1 tabular-nums opacity-80">
                    · {formatDuration(sinceSec)}{" "}
                    {z.status === "PROBLEM" ? "지속" : "불일치"}
                  </span>
                )}
              </span>
            </li>
          );
        })}
      </ul>

      {openCamera && (
        <CameraVideoModal
          cameraId={openCamera.cameraId}
          zoneName={openCamera.zoneName}
          onClose={() => setOpenCamera(null)}
        />
      )}
    </>
  );
}
